import { useEffect, useState } from 'react'
import { ExternalLink, Loader2, Play, Plane, Route, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { toast } from '@/hooks/use-toast'
import type { AirportDetail, AtcFeed, WatchRuleInput } from '@/types/airTraffic'
import type { SelectionOverlay } from '../AirMap'
import { AIR, CLASS_COLOR, FONT_MONO, classify, fmtAlt } from '../theme'
import { ActionButton, Chip, Empty, KV, SectionLabel, downloadReport, errorText } from '../ui'
import useRoutes, { routeLabel, routeLines } from '../useRoutes'

const RADIUS_KM = 50
// ICAO's realtime NOTAM API has been down (503) for days and FAA's search
// blocks servers, so NOTAMs open in the analyst's own browser instead.
const NOTAM_SEARCH_URL = 'https://notams.aim.faa.gov/notamSearch/nsapp.html#/'

interface Props {
  icao: string
  watched: Set<string>
  playing: AtcFeed | null
  onPlay: (f: AtcFeed) => void
  onClose: () => void
  onSelection: (s: SelectionOverlay) => void
  onOpenAircraft: (hex: string, lon?: number, lat?: number) => void
  onWatch: (rule: WatchRuleInput) => Promise<void>
}

export default function AirportInspector({ icao, watched, playing, onPlay, onClose, onSelection, onOpenAircraft, onWatch }: Props) {
  const [d, setD] = useState<AirportDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { routes, busy: routesBusy, load: loadRoutes } = useRoutes()

  useEffect(() => {
    let cancelled = false
    setD(null)
    setError(null)
    const load = () =>
      apiClient
        .airAirport(icao, RADIUS_KM)
        .then((r) => !cancelled && setD(r))
        .catch((e) => !cancelled && setError(errorText(e)))
    load()
    // traffic around the field changes constantly; refresh while open
    const id = window.setInterval(load, 20000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [icao])

  useEffect(() => {
    if (d) onSelection({ ring: { lon: d.airport.lon, lat: d.airport.lat, km: d.radius_km }, routes: routeLines(routes) })
  }, [d, routes, onSelection])

  const ap = d?.airport
  const withCallsign = (d?.traffic || []).filter((a) => a.cs)
  const missing = withCallsign.filter((a) => !(a.hex in routes))

  const openNotams = () => {
    navigator.clipboard?.writeText(icao).catch(() => null)
    toast({ title: `${icao} copied`, description: 'Paste it into the FAA NOTAM search location box.' })
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <div className="min-w-0">
          <div className="text-[18px] font-semibold leading-tight" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
            {ap ? `${ap.icao}${ap.iata ? ` / ${ap.iata}` : ''}` : icao}
          </div>
          <div className="truncate text-[12px]" style={{ color: AIR.muted }}>
            {ap ? `${ap.name}${ap.city ? `, ${ap.city}` : ''} ${ap.country || ''}` : 'Loading airport…'}
          </div>
          {ap?.mil && (
            <div className="mt-1.5">
              <Chip color={AIR.military}>Military airfield</Chip>
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-sm p-1 hover:bg-white/5">
          <X className="h-4 w-4" style={{ color: AIR.muted }} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {error && <Empty>{error}</Empty>}
        {!d && !error && (
          <div className="flex items-center gap-2 px-3 py-4 text-[12px]" style={{ color: AIR.muted }}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}

        {d && (
          <>
            <SectionLabel>ATC audio</SectionLabel>
            {d.atc_feeds.length === 0 && (
              <Empty>
                No LiveATC feed catalogued for {ap?.icao}.{' '}
                <a href={d.liveatc_search_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline-offset-2 hover:underline" style={{ color: AIR.text }}>
                  Find on LiveATC <ExternalLink className="h-3 w-3" />
                </a>
              </Empty>
            )}
            {d.atc_feeds.map((f) => {
              const on = playing?.mount === f.mount
              return (
                <button
                  key={f.mount}
                  type="button"
                  onClick={() => onPlay(f)}
                  className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-white/[0.03]"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm" style={{ background: on ? AIR.accent : AIR.panel2 }}>
                    <Play className="h-3 w-3" style={{ color: on ? AIR.bg : AIR.text }} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[12px]" style={{ color: AIR.bright }}>
                      {f.name}
                    </span>
                    <span className="block truncate text-[11px]" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                      {[f.kind, ...f.freqs].join(' · ')}
                    </span>
                  </span>
                </button>
              )
            })}

            <SectionLabel right={<span className="text-[10px]" style={{ color: AIR.dim }}>within {d.radius_km} km</span>}>
              Traffic now · {d.composition.total}
            </SectionLabel>
            <div className="flex flex-wrap items-center gap-1 px-3 pb-1.5">
              {d.composition.military > 0 && <Chip color={AIR.military}>{d.composition.military} military</Chip>}
              {d.composition.privacy_listed > 0 && <Chip color={AIR.privacy}>{d.composition.privacy_listed} privacy</Chip>}
              {d.composition.emergencies > 0 && <Chip color={AIR.emergency}>{d.composition.emergencies} emergency</Chip>}
            </div>
            {withCallsign.length > 0 && (
              <div className="px-3 pb-1.5">
                <ActionButton
                  onClick={() => loadRoutes(missing.map((a) => a.hex))}
                  busy={routesBusy}
                  disabled={!missing.length}
                  title="Origin and destination for every aircraft here, from Flightradar24. Lookups are cached for 10 minutes."
                >
                  <Route className="h-3.5 w-3.5" />
                  {missing.length ? `Show routes · ~${Math.min(missing.length, 60)} FR24 credits` : 'Routes shown'}
                </ActionButton>
              </div>
            )}
            {d.traffic.length === 0 && <Empty>No aircraft received within {d.radius_km} km right now.</Empty>}
            {d.traffic.slice(0, 40).map((a) => {
              const cls = classify(a, watched)
              const r = routes[a.hex]
              return (
                <button
                  key={a.hex}
                  type="button"
                  onClick={() => onOpenAircraft(a.hex, a.lon, a.lat)}
                  className="flex w-full items-center gap-2 px-3 py-1 text-left text-[12px] hover:bg-white/[0.03]"
                  title={r ? [r.flight, r.type, r.operator].filter(Boolean).join(' · ') : undefined}
                >
                  <Plane className="h-3 w-3 shrink-0" style={{ color: CLASS_COLOR[cls], transform: `rotate(${(a.trk ?? 45) - 45}deg)` }} />
                  <span className="w-[72px] shrink-0 truncate" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
                    {a.cs || a.reg || a.hex.toUpperCase()}
                  </span>
                  <span className="w-[74px] shrink-0 truncate" style={{ color: r ? AIR.watched : AIR.muted, fontFamily: FONT_MONO }}>
                    {routeLabel(r) || a.type || r?.type || '—'}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-right" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                    {fmtAlt(a)} · {a.dist_km} km
                  </span>
                </button>
              )
            })}

            <SectionLabel>NOTAMs</SectionLabel>
            <div className="px-3 text-[12px]" style={{ color: AIR.muted }}>
              <a
                href={NOTAM_SEARCH_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={openNotams}
                className="inline-flex items-center gap-1 underline-offset-2 hover:underline"
                style={{ color: AIR.text }}
              >
                Search {icao} NOTAMs on FAA NOTAM Search <ExternalLink className="h-3 w-3" />
              </a>
              <div className="pt-0.5 text-[11px]">Covers international airports. The code is copied for you to paste.</div>
            </div>

            <SectionLabel>Airport</SectionLabel>
            <KV k="Position" v={ap ? `${ap.lat.toFixed(4)}, ${ap.lon.toFixed(4)}` : undefined} />
            <KV k="Top types" v={Object.keys(d.composition.by_type).filter((t) => t !== 'Unknown').slice(0, 5).join(', ')} />
            <KV k="Top operators" v={Object.keys(d.composition.by_operator).slice(0, 3).join(', ')} mono={false} />
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 border-t px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <ActionButton tone="primary" onClick={() => downloadReport('airport', { code: icao, radius_km: RADIUS_KM }, icao)}>
          Report
        </ActionButton>
        <ActionButton
          onClick={() => onWatch({ kind: 'airport', value: icao, radius_km: 40, match: 'notable' })}
          title="Alert when a military, privacy-listed or emergency aircraft comes within 40 km"
        >
          Watch airport
        </ActionButton>
      </div>
    </div>
  )
}
